import React from 'react';
import '../QuizTaker.css';

interface QuizConfig {
  seed: number;
  questionCount: number;
  chapter: string;
  book: string;
  questionType: 'true_false' | 'multiple_choice';
}

interface QuizConfigProps {
  quizConfig: QuizConfig;
  questionCountInput: string;
  availableChapters: string[];
  bookTitle: string;
  onConfigChange: (config: QuizConfig) => void;
  onQuestionCountInputChange: (value: string) => void;
  onStartQuiz: () => void;
  onBack: () => void;
}

const QuizConfigComponent: React.FC<QuizConfigProps> = ({
  quizConfig,
  questionCountInput,
  availableChapters,
  bookTitle,
  onConfigChange,
  onQuestionCountInputChange,
  onStartQuiz,
  onBack
}) => {
  // Generate shareable URL
  const generateShareableUrl = () => {
    const params = new URLSearchParams({
      seed: quizConfig.seed.toString(),
      q: quizConfig.questionCount.toString(),
      chapter: quizConfig.chapter,
      book: quizConfig.book,
      type: quizConfig.questionType
    });
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  };

  // Copy URL to clipboard
  const copyShareableUrl = async () => {
    try {
      await navigator.clipboard.writeText(generateShareableUrl());
      alert('Quiz URL copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback: select the URL text
      const urlInput = document.createElement('input');
      urlInput.value = generateShareableUrl();
      document.body.appendChild(urlInput);
      urlInput.select();
      document.execCommand('copy');
      document.body.removeChild(urlInput);
      alert('Quiz URL copied to clipboard!');
    }
  };

  // Generate new quiz with random seed
  const generateNewQuiz = () => {
    const newConfig = {
      ...quizConfig,
      seed: Math.floor(Math.random() * 1000000)
    };
    onConfigChange(newConfig);

    // Update URL
    const params = new URLSearchParams({
      seed: newConfig.seed.toString(),
      q: newConfig.questionCount.toString(),
      chapter: newConfig.chapter,
      book: newConfig.book,
      type: newConfig.questionType
    });
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  return (
    <div className="content-container">
      <div className="quiz-header">
        <h2>Quiz Configuration</h2>
        <h3>{bookTitle}</h3>
        <button onClick={onBack} className="btn btn-secondary">
          Back to Quiz
        </button>
      </div>

      <div className="quiz-config">
        <div className="config-section">
          <h3>Quiz Settings</h3>
          <div className="config-item">
            <label htmlFor="question-count-input">Number of Questions:</label>
            <input
              type="text"
              value={questionCountInput}
              onChange={(e) => {
                const value = e.target.value;
                onQuestionCountInputChange(value);
                const numValue = parseInt(value);
                if (!isNaN(numValue) && numValue > 0) {
                  onConfigChange({ ...quizConfig, questionCount: numValue });
                }
              }}
              className="config-input"
              id="question-count-input"
            />
          </div>
          <div className="config-item">
            <label htmlFor="chapter-select">Chapter:</label>
            <select
              value={quizConfig.chapter}
              onChange={(e) => onConfigChange({ ...quizConfig, chapter: e.target.value })}
              className="config-select"
              id="chapter-select"
            >
              <option value="all">All questions</option>
              {availableChapters.map(chapterId => (
                <option key={chapterId} value={chapterId}>Chapter {chapterId}</option>
              ))}
            </select>
          </div>
          <div className="config-item">
            <label htmlFor="question-type-select">Question Type:</label>
            <select
              value={quizConfig.questionType}
              onChange={(e) => {
                const newType = e.target.value as 'true_false' | 'multiple_choice';
                onConfigChange({ ...quizConfig, questionType: newType });
              }}
              className="config-select"
              id="question-type-select"
            >
              <option value="true_false">True/False</option>
              <option value="multiple_choice">Multiple Choice</option>
            </select>
          </div>
          <div className="config-item">
            <label htmlFor="seed-input">Random Seed:</label>
            <input
              type="number"
              value={quizConfig.seed}
              onChange={(e) => onConfigChange({ ...quizConfig, seed: parseInt(e.target.value) || 0 })}
              className="config-input"
              id="seed-input"
            />
            <button onClick={generateNewQuiz} className="btn btn-primary btn-small">
              Generate New Quiz
            </button>
          </div>
        </div>

        <div className="config-section">
          <h3>Share Quiz</h3>
          <div className="share-url">
            <input
              type="text"
              value={generateShareableUrl()}
              readOnly
              className="url-input"
            />
            <button onClick={copyShareableUrl} className="btn btn-secondary">
              Copy URL
            </button>
          </div>
          <p className="url-info">
            Share this URL to let others take the same quiz with the same questions.
          </p>
        </div>

        <div className="config-actions">
          <button onClick={onStartQuiz} className="btn btn-primary" id="start-quiz-button">
            Start Quiz
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizConfigComponent; 